import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ALL_VIEW_ID, BUILT_IN_VIEW_CRITERION, PAGE_SIZES, QUICK_VIEW_LIMIT, SAVED_VIEW_PARAM, TAG_FILTER_CRITERION, builtInColumnKeys, builtInCriteria, builtInKeyOf, builtInNarrows, effectiveViewId, emptyView, quickViews, readSavedCriteria, readSavedSort, starRefused, starRowCriteria, switcherEntries, switcherOrder, t, withFixedFirst,
  type BuiltInView, type ColumnType, type CriteriaRow, type NewViewDraft, type PageSize, type SortState, type SwitcherEntry,
} from '@bms2/shared';
import { SAVED_VIEWS_PATH, del, post, put, savedViewPath, useRows } from '../../pages/records/api';
import { NewViewModal, type ViewColumn } from '../modals/NewViewModal';
import type { CriteriaAttribute } from '../modals/ViewCriteriaRows';

/**
 * 🔒 A list's saved views, read from and written to the one store (piece 12;
 * decisions 2170, 2171, 2174). The record lists and every other list that draws
 * the switcher use this same hook, so a view is kept, edited, deleted, opened
 * by default and carried in the address the same way everywhere.
 */

/** A view as the switcher and the window read it. */
export interface StoredView {
  id: string;
  version: number;
  name: string;
  /** Column keys in the order picked (decision 2173); empty draws the list's own set. */
  columns: string[];
  criteria: CriteriaRow[];
  sort: SortState | null;
  pageSize: PageSize | null;
  /** 🔒 The list's Tags filter the view keeps, as the address writes it (T1581); null where it keeps none. */
  tagFilter: string | null;
  isDefault: boolean;
  isFavourite: boolean;
  /** 🔒 The built-in view this row holds the person's star for, or null where the row is a view of their own (decision 2880). */
  builtInKey?: string | null;
}

/** The store's answer for one view (piece 12). */
interface StoredViewAnswer {
  id: string;
  version: number;
  name: string;
  columns: string[] | null;
  /** The wire holds the column as JSON; `readSavedCriteria` and `readSavedSort` validate it once (T2171). */
  criteria: unknown;
  sort: unknown;
  pageSize: number | null;
  isDefault: boolean;
  isFavourite: boolean;
}

function storedOf(answer: StoredViewAnswer): StoredView {
  const rows = readSavedCriteria(answer.criteria);
  // 🔒 The Tags filter rides in the conditions under a field no column has, and leaves them here, so no condition row draws it (T1581).
  const tagRow = rows.find((row) => row.field === TAG_FILTER_CRITERION);
  return {
    id: answer.id,
    version: answer.version,
    name: answer.name,
    columns: Array.isArray(answer.columns) ? answer.columns : [],
    // 🔒 A star row's built-in key leaves the conditions the same way (decision 2880).
    builtInKey: builtInKeyOf(rows),
    criteria: rows.filter((row) => row.field !== TAG_FILTER_CRITERION && row.field !== BUILT_IN_VIEW_CRITERION),
    tagFilter: tagRow && typeof tagRow.value === 'string' && tagRow.value !== '' ? tagRow.value : null,
    sort: readSavedSort(answer.sort),
    pageSize: (PAGE_SIZES as readonly number[]).includes(answer.pageSize ?? -1) ? (answer.pageSize as PageSize) : null,
    isDefault: answer.isDefault === true,
    isFavourite: answer.isFavourite === true,
  };
}

export interface SavedViewsOptions {
  /** The list's own scope; null keeps no store, and the list offers no Add New View. */
  scopeKey: string | null;
  apiBaseUrl: string;
  /** 🔒 Choosing a view restores its sort and its rows per page (decision 2174). */
  onApply?: (view: StoredView) => void;
  /** 🔒 The list's built-in views, All first, from its register (decision 2880); a list that declares none reads All alone. */
  builtIns?: readonly BuiltInView[];
}

/** What a view keeps from the list beside its window's draft: the sort and rows per page (decision 2174), and the Tags filter where the list has one (T1581). */
export interface SavedViewExtras {
  sort: SortState | null;
  pageSize: PageSize | null;
  tagFilter?: string | null;
}

/** 🔒 Edit View names the built-in view it edits where the view is one (decision 2981); its `view` is the person's star row, or the register's view drawn as a row until they save. */
export type ViewWindow = { mode: 'add'; criteria?: readonly CriteriaRow[] } | { mode: 'edit'; view: StoredView; builtIn?: BuiltInView };

export interface SavedViews {
  /** The person's views on this list, as stored, without the rows that hold a star on a built-in view. */
  views: readonly StoredView[];
  /** The views in the switcher's order, starred first (decision 2171). */
  ordered: readonly StoredView[];
  /** 🔒 The list's built-in views, as its register declares them (decision 2880). */
  builtIns: readonly BuiltInView[];
  /** 🔒 Every view the switcher lists, built-in and own, with each star as it stands for this person (decisions 2171, 2880). */
  switcher: readonly SwitcherEntry[];
  /** 🔒 The starred views, which the bar above the list draws (decision 2880). */
  quick: readonly SwitcherEntry[];
  /** The view showing, or All. */
  currentId: string;
  current: StoredView | null;
  /** 🔒 The built-in view showing, All among them, or null where a view of the person's own shows (decision 2880). */
  currentBuiltIn: BuiltInView | null;
  /** 🔒 The column keys the view showing draws: its own, or a built-in view's from the person's star row; empty draws the list's own set (decisions 2173, 2981). */
  columnKeys: readonly string[];
  /** 🔒 Stars or unstars a view, built-in or own; a sixth star is refused by name (decision 2880). */
  star: (id: string) => Promise<void>;
  /** The refusal of the last star, in the switcher's own words, or null. */
  starNotice: string | null;
  /** The store has answered, or there is none; until then a list that reads by its view waits. */
  settled: boolean;
  /** What the address names: a view id, All, or nothing (decision 2171). */
  addressed: string | null;
  setAddressed: (id: string | null) => void;
  choose: (id: string) => void;
  window: ViewWindow | null;
  /** 🔒 Add New View, with the Items list's Filter conditions already in its rows where Save as View opens it (decision 8800). */
  openNew: (seed?: { criteria: readonly CriteriaRow[] }) => void;
  openEdit: (id: string) => void;
  close: () => void;
  /** Writes the window's draft; answers the refusal to read inside the window, or null once the write is accepted. */
  save: (draft: NewViewDraft, extras: SavedViewExtras) => Promise<string | null>;
  remove: (id: string) => Promise<string | null>;
}

const NO_BUILT_INS: readonly BuiltInView[] = [];

export function useSavedViews({ scopeKey, apiBaseUrl, onApply, builtIns = NO_BUILT_INS }: SavedViewsOptions): SavedViews {
  const read = useRows<StoredViewAnswer>(scopeKey ? `${SAVED_VIEWS_PATH}?scopeKey=${encodeURIComponent(scopeKey)}` : null, apiBaseUrl);
  const answered = read.state;
  const stored = useMemo(() => (answered.kind === 'ready' ? answered.data.map(storedOf) : []), [answered]);
  /* 🔒 A row naming a built-in view holds the person's star on it and is never a view of their own (decision 2880). */
  const views = useMemo(() => stored.filter((view) => !view.builtInKey), [stored]);
  const starRows = useMemo(() => new Map(stored.filter((view) => view.builtInKey).map((view) => [view.builtInKey as string, view])), [stored]);
  const ordered = useMemo(() => switcherOrder(views), [views]);
  const switcher = useMemo(() => switcherEntries(builtIns, views, new Map([...starRows].map(([key, row]) => [key, row.isFavourite]))), [builtIns, views, starRows]);
  const quick = useMemo(() => quickViews(switcher), [switcher]);
  const settled = scopeKey === null || answered.kind !== 'loading';
  const [addressed, setAddressed] = useState<string | null>(null);
  const builtInKeys = useMemo(() => builtIns.map((view) => view.key), [builtIns]);
  const currentId = settled ? effectiveViewId(views, addressed, builtInKeys) : ALL_VIEW_ID;
  const current = views.find((view) => view.id === currentId) ?? null;
  const currentBuiltIn = current ? null : builtIns.find((view) => view.key === currentId) ?? null;
  const columnKeys = current ? current.columns : currentBuiltIn ? builtInColumnKeys(starRows, currentBuiltIn.key) : [];
  const [starNotice, setStarNotice] = useState<string | null>(null);
  const [opened, setOpened] = useState<ViewWindow | null>(null);

  // 🔒 Each view applies its sort and rows per page once as it opens, and again only after it is saved over.
  const apply = useRef(onApply);
  useEffect(() => {
    apply.current = onApply;
  });
  const applied = useRef<string | null>(null);
  useEffect(() => {
    const stamp = current ? `${current.id}@${current.version}` : null;
    if (stamp === applied.current) return;
    applied.current = stamp;
    if (current) apply.current?.(current);
  }, [current]);

  const choose = useCallback((id: string) => setAddressed(id), []);
  const reload = read.reload;

  /**
   * 🔒 A built-in view's Save (decision 2981): the person's own star row naming it, with the
   * columns they arranged and the star as the window leaves it. The row is made on the first
   * save and written over at the version read after; the name and the conditions are the
   * register's and are written as it declares them, and no sort or page size is kept.
   */
  const saveBuiltIn = useCallback(
    async (builtIn: BuiltInView, view: StoredView, draft: NewViewDraft): Promise<string | null> => {
      if (!scopeKey) return t('view.saveFailed');
      const entry = switcher.find((e) => e.id === builtIn.key);
      if (draft.isFavourite && entry && starRefused(switcher, builtIn.key)) return `${builtIn.name} ${t('view.starLimit')}`;
      const body = { name: builtIn.name, columns: [...draft.selectedColumns], criteria: starRowCriteria(builtIn), isFavourite: draft.isFavourite };
      const row = starRows.get(builtIn.key) ?? null;
      const answer = row
        ? await put<{ id: string }>(savedViewPath(row.id), { ...body, version: view.version }, apiBaseUrl)
        : await post<{ id: string }>(SAVED_VIEWS_PATH, { scopeKey, ...body, isDefault: false }, apiBaseUrl);
      if (!answer.ok) return answer.message || t('view.saveFailed');
      reload();
      setAddressed(builtIn.key);
      setOpened(null);
      return null;
    },
    [scopeKey, switcher, starRows, apiBaseUrl, reload],
  );

  const save = useCallback(
    async (draft: NewViewDraft, extras: SavedViewExtras): Promise<string | null> => {
      if (!scopeKey || !opened) return t('view.saveFailed');
      if (opened.mode === 'edit' && opened.builtIn) return saveBuiltIn(opened.builtIn, opened.view, draft);
      // 🔒 A sixth star is refused from the window as from the switcher, by name and before any write (decision 2880); keeping a starred view starred is no new star.
      const newlyStarred = draft.isFavourite && !(opened.mode === 'edit' && opened.view.isFavourite);
      const full = opened.mode === 'edit' ? starRefused(switcher, opened.view.id) : switcher.filter((entry) => entry.starred).length >= QUICK_VIEW_LIMIT;
      // The refusal names the view as it is saved: an edit that renamed it still refuses the view the person pressed (T2191).
      if (newlyStarred && full) return `${opened.mode === 'edit' ? opened.view.name : draft.name.trim()} ${t('view.starLimit')}`;
      // A list that draws no Tags menu, such as the same list in a pane, keeps the filter the view already carries.
      const tags = extras.tagFilter !== undefined ? extras.tagFilter : opened.mode === 'edit' ? opened.view.tagFilter : null;
      const kept = draft.criteria.filter((row) => row.field !== TAG_FILTER_CRITERION).map(({ id, field, comparator, value }) => ({ id, field, comparator, value }));
      const body = {
        name: draft.name.trim(),
        columns: [...draft.selectedColumns],
        criteria: tags ? [...kept, { id: TAG_FILTER_CRITERION, field: TAG_FILTER_CRITERION, comparator: 'is' as const, value: tags }] : kept,
        sort: extras.sort,
        pageSize: extras.pageSize,
        isFavourite: draft.isFavourite,
        isDefault: draft.isDefault,
      };
      const answer =
        opened.mode === 'edit'
          ? await put<{ id: string }>(savedViewPath(opened.view.id), { ...body, version: opened.view.version }, apiBaseUrl)
          : await post<{ id: string }>(SAVED_VIEWS_PATH, { scopeKey, ...body }, apiBaseUrl);
      if (!answer.ok) return answer.message || t('view.saveFailed');
      reload();
      setAddressed(answer.data.id);
      setOpened(null);
      return null;
    },
    [scopeKey, opened, switcher, apiBaseUrl, reload, saveBuiltIn],
  );

  const remove = useCallback(
    async (id: string): Promise<string | null> => {
      const answer = await del<{ ok: boolean }>(savedViewPath(id), apiBaseUrl);
      if (!answer.ok) return answer.message || t('view.deleteFailed');
      // 🔒 Deleting the view the list is showing returns the list to All (decision 2170).
      if (currentId === id) setAddressed(ALL_VIEW_ID);
      reload();
      setOpened(null);
      return null;
    },
    [apiBaseUrl, currentId, reload],
  );

  /**
   * 🔒 The star is the tick (decisions 2171, 2880). A person's own view writes its
   * favourite flag; a built-in view writes the person's own row naming it, so a
   * built-in starred for everyone is unstarred by a row that says so. A sixth
   * star on one list is refused by name, and nothing is written.
   */
  // 🔒 A second press while a star's write is in flight is ignored: both would write from the one read, and the second would answer 409 (T2191).
  const starring = useRef(false);
  const star = useCallback(
    async (id: string): Promise<void> => {
      const entry = switcher.find((e) => e.id === id);
      if (!scopeKey || !entry || starring.current) return;
      // The last press's notice goes with the next press, whatever that press does (T2191).
      setStarNotice(null);
      if (starRefused(switcher, id)) {
        setStarNotice(`${entry.name} ${t('view.starLimit')}`);
        return;
      }
      const builtIn = entry.builtIn ? builtIns.find((view) => view.key === id) ?? null : null;
      const row = builtIn ? starRows.get(builtIn.key) ?? null : views.find((view) => view.id === id) ?? null;
      starring.current = true;
      let answer;
      try {
        answer = row
          ? await put<{ id: string }>(savedViewPath(row.id), { isFavourite: !entry.starred, version: row.version }, apiBaseUrl)
          : builtIn
            ? await post<{ id: string }>(SAVED_VIEWS_PATH, { scopeKey, name: builtIn.name, columns: [], criteria: starRowCriteria(builtIn), isFavourite: !entry.starred, isDefault: false }, apiBaseUrl)
            : null;
      } finally {
        starring.current = false;
      }
      if (!answer) return;
      setStarNotice(answer.ok ? null : answer.message || t('view.saveFailed'));
      if (answer.ok) reload();
    },
    [scopeKey, switcher, builtIns, starRows, views, apiBaseUrl, reload],
  );

  return {
    views,
    ordered,
    builtIns,
    switcher,
    quick,
    currentId,
    current,
    currentBuiltIn,
    columnKeys,
    star,
    starNotice,
    settled,
    addressed,
    setAddressed,
    choose,
    window: opened,
    openNew: (seed) => setOpened(seed && seed.criteria.length > 0 ? { mode: 'add', criteria: seed.criteria } : { mode: 'add' }),
    openEdit: (id) => {
      const view = views.find((v) => v.id === id);
      if (view) return setOpened({ mode: 'edit', view });
      // 🔒 A built-in view opens on the person's star row, or the register's view drawn as a row until they save (decision 2981).
      const builtIn = builtIns.find((b) => b.key === id);
      if (!builtIn) return;
      const row = starRows.get(builtIn.key);
      const starred = switcher.find((e) => e.id === builtIn.key)?.starred === true;
      setOpened({
        mode: 'edit',
        builtIn,
        view: {
          id: row?.id ?? builtIn.key, version: row?.version ?? 0, name: builtIn.name, columns: row?.columns ?? [], criteria: builtInCriteria(builtIn), sort: null, pageSize: null, tagFilter: null,
          isDefault: false, isFavourite: starred, builtInKey: builtIn.key,
        },
      });
    },
    close: () => setOpened(null),
    save,
    remove,
  };
}

/**
 * 🔒 The chosen view rides in the list's address (decision 2171), so a reload,
 * Back and a copied link keep it. It draws nothing; it keeps the address and the
 * list's choice in step, each way, and replaces the entry rather than adding one
 * so Back leaves the list rather than stepping through its views.
 */
export function SavedViewAddress({ value, onChange, param = SAVED_VIEW_PARAM }: { value: string | null; onChange: (id: string | null) => void; param?: string }) {
  const [params, setParams] = useSearchParams();
  const inAddress = params.get(param);
  const previous = useRef(value);
  useEffect(() => {
    if (value === previous.current) return;
    previous.current = value;
    if (value === inAddress) return;
    setParams(
      (was) => {
        const next = new URLSearchParams(was);
        if (value === null) next.delete(param);
        else next.set(param, value);
        return next;
      },
      { replace: true },
    );
    // The address is written when the choice moves, never when the address does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  useEffect(() => {
    if (inAddress === value) return;
    previous.current = inAddress;
    onChange(inAddress);
    // The choice follows the address when the address moves, never when the choice does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inAddress]);
  return null;
}

/** The words for a sort's direction, by the column's type: A to Z, lowest first, oldest first. */
function directionWords(type: ColumnType | undefined, direction: 'asc' | 'desc'): string {
  if (type === 'number' || type === 'currency') return t(direction === 'asc' ? 'view.sort.numberAsc' : 'view.sort.numberDesc');
  if (type === 'date' || type === 'instant') return t(direction === 'asc' ? 'view.sort.dateAsc' : 'view.sort.dateDesc');
  return t(direction === 'asc' ? 'view.sort.textAsc' : 'view.sort.textDesc');
}

/** 🔒 The one line under the columns naming the sort and rows per page the view keeps (decision 2174): *Sorted by Name, A to Z, 50 rows a page*. */
export function sortSummary(sort: SortState | null, pageSize: PageSize | null, columns: readonly ViewColumn[]): string {
  const rows = `${pageSize ?? PAGE_SIZES[0]} ${t('view.sort.rows')}`;
  const column = sort ? columns.find((c) => c.key === sort.key) : undefined;
  if (!sort || !column) return `${t('view.sort.none')}, ${rows}`;
  return `${t('view.sort.by')} ${column.label}, ${directionWords(column.type, sort.direction)}, ${rows}`;
}

function draftOf(view: StoredView, columns: readonly ViewColumn[]): NewViewDraft {
  const fixed = columns[0]?.key ?? '';
  const known = new Set(columns.map((c) => c.key));
  const picked = view.columns.filter((key) => known.has(key));
  return {
    name: view.name,
    isFavourite: view.isFavourite,
    isDefault: view.isDefault,
    criteria: view.criteria,
    // 🔒 A view naming columns opens on those it still finds, the fixed first column leading, exactly as the list draws it (T1411); only a view naming none opens on the list's own set.
    selectedColumns: view.columns.length > 0 ? withFixedFirst(picked, fixed) : columns.filter((c) => !c.offByDefault).map((c) => c.key),
  };
}

export interface SavedViewWindowProps {
  store: SavedViews;
  /** Every column the list offers, in its own order; the first is the fixed one. */
  columns: readonly ViewColumn[];
  /** The list's plural, for the empty band: *All companies show.* */
  plural: string;
  /** The list's sort and rows per page now, which a view saved from it keeps (decision 2174). */
  sort: SortState | null;
  pageSize: PageSize;
  scope?: 'record' | 'itemTable';
  /** 🔒 The list's Tags filter now, as the address writes it, and the line naming it, which a view saved from it keeps (T1581). */
  tagFilter?: { value: string | null; summary: string | null; summaryOf: (value: string | null) => string | null };
  /** 🔒 The Items list's attribute types, which the window's condition rows offer after the columns (decision 8800). */
  attributes?: readonly CriteriaAttribute[] | undefined;
  /** 🔒 Told once a view opened by Save as View is saved, so the list moves the Filter's conditions into it (decision 8800). */
  onSeededSave?: () => void;
}

/**
 * The New View Modal as Add New View and Edit View (decisions 2170, 2175),
 * mounted only while open and keyed to the view it edits, so a second opening
 * never shows the first one's draft.
 */
export function SavedViewWindow({ store, columns, plural, sort, pageSize, scope = 'record', tagFilter, attributes, onSeededSave }: SavedViewWindowProps) {
  const open = store.window;
  if (!open) return null;
  const view = open.mode === 'edit' ? open.view : null;
  // A view edited from the switcher while another shows keeps its own sort; the one showing keeps the list's.
  const builtIn = open.mode === 'edit' ? open.builtIn : undefined;
  const keepsList = view === null || view.id === store.currentId || (builtIn !== undefined && builtIn.key === store.currentId);
  const savedSort = keepsList ? sort : view.sort;
  const savedPageSize = keepsList ? pageSize : view.pageSize;
  const savedTags = builtIn ? null : tagFilter ? (keepsList ? tagFilter.value : view.tagFilter) : null;
  const tagLine = tagFilter && savedTags ? (keepsList ? tagFilter.summary : tagFilter.summaryOf(savedTags)) : null;
  const summary = sortSummary(savedSort, savedPageSize, columns);
  const seeded = open.mode === 'add' && open.criteria ? [...open.criteria] : null;
  return (
    <NewViewModal
      key={view ? `edit-${view.id}-${view.version}` : 'add'}
      open
      mode={view ? 'edit' : 'add'}
      columns={columns}
      scope={scope}
      plural={plural}
      // 🔒 A built-in view's name is taken too, so no view of the person's own shares it in the switcher (decisions 2170, 2880).
      takenNames={builtIn ? [] : [...store.builtIns.map((b) => ({ id: b.key, name: b.name })), ...store.views.filter((v) => v.id !== view?.id)]}
      {...(builtIn ? {} : { sortSummary: tagLine ? `${summary}, ${t('tagFilter.tagged')} ${tagLine}` : summary })}
      {...(view ? { initial: draftOf(view, columns) } : seeded ? { initial: { ...emptyView(columns[0]?.key ?? ''), criteria: seeded } } : {})}
      {...(attributes ? { attributes } : {})}
      {...(view && !builtIn ? { onDelete: () => store.remove(view.id) } : {})}
      {...(builtIn ? { builtIn: { narrows: builtInNarrows(builtIn) } } : {})}
      onCancel={store.close}
      onSave={async (draft) => {
        const refused = await store.save(draft, { sort: savedSort, pageSize: savedPageSize, ...(tagFilter ? { tagFilter: savedTags } : {}) });
        if (refused === null && seeded) onSeededSave?.();
        return refused;
      }}
    />
  );
}
